/**
 * GUMSHOE — GAME module. Single source of truth for GameState (immutable
 * updates), case logic for "The Last Set at the Blue Dahlia", the clue
 * dependency graph, accusation rules, and the monologue script.
 *
 * Emits:    clue:collected, monologue:play, game:accuse, game:won, game:lost
 * Consumes: player:interact, title:dismissed, zone:changed,
 *           ui:journal-toggle, monologue:done
 *
 * Owned by GAME; see CONTRACTS.md.
 */
import type { GameState, InteractTarget, ModuleContext, ZoneId } from '../core/types'
import {
  BEAT_BOOTH_EARLY,
  BEAT_BOOTH_READY,
  BEAT_CLUE_LOCKED,
  BEAT_INTRO,
  BEAT_OFFICE,
  MONOLOGUE_BEATS
} from './data'
import { createMonologueQueue } from './monologue'
import {
  allRealCluesCollected,
  CLUE_ID_SET,
  createInitialState,
  findClue,
  findSuspect,
  hasRequiredClues,
  isCollected,
  prereqsMet,
  SUSPECT_ID_SET,
  withAccusation,
  withClue
} from './state'
import { isPhoneBoothTarget, resolveClueId, resolveSuspectId } from './targets'

export const initGame = (ctx: ModuleContext): void => {
  const { bus, debug } = ctx

  let state: GameState = createInitialState()
  /** Beats that fire at most once per case (intro, office entrance). */
  let firedOnceBeatIds: readonly string[] = []

  const queue = createMonologueQueue(bus)

  const playBeat = (beatId: string): void => {
    const beatToPlay = MONOLOGUE_BEATS.find((b) => b.id === beatId)
    if (beatToPlay) queue.enqueue(beatToPlay)
  }

  const playBeatOnce = (beatId: string): void => {
    if (firedOnceBeatIds.includes(beatId)) return
    firedOnceBeatIds = [...firedOnceBeatIds, beatId]
    playBeat(beatId)
  }

  // -------------------------------------------------------------------------
  // Case actions — debug hooks share these exact code paths
  // -------------------------------------------------------------------------

  const collect = (clueId: string): void => {
    if (state.phase === 'won' || state.phase === 'lost') return
    const clue = findClue(clueId)
    if (!clue || isCollected(state, clueId)) return
    if (!prereqsMet(state, clueId)) {
      playBeat(BEAT_CLUE_LOCKED)
      return
    }
    state = withClue(state, clueId)
    bus.emit('clue:collected', { clue })
    if (clue.monologueId !== undefined) playBeat(clue.monologueId)
  }

  const accuse = (suspectId: string): void => {
    if (state.phase !== 'investigating') return
    const suspect = findSuspect(suspectId)
    if (!suspect) return
    bus.emit('game:accuse', { suspectId })
    const won = suspect.isGuilty && hasRequiredClues(state, suspect)
    state = withAccusation(state, suspectId, won)
    bus.emit(won ? 'game:won' : 'game:lost', { suspectId })
  }

  const handlePhoneBooth = (): void => {
    if (state.phase !== 'investigating') return
    if (allRealCluesCollected(state)) {
      // UI opens its accuse panel from the same player:interact event;
      // emitting ui:journal-toggle here would freeze PLAYER with no
      // matching UI close path (integration fix — see CONTRACTS.md).
      playBeat(BEAT_BOOTH_READY)
      return
    }
    playBeat(BEAT_BOOTH_EARLY)
  }

  const handleInteract = (target: InteractTarget): void => {
    const clueId = resolveClueId(target, CLUE_ID_SET)
    if (clueId !== null) {
      collect(clueId)
      return
    }
    const suspectId = resolveSuspectId(target, SUSPECT_ID_SET)
    if (suspectId !== null) {
      accuse(suspectId)
      return
    }
    if (isPhoneBoothTarget(target)) handlePhoneBooth()
  }

  // -------------------------------------------------------------------------
  // Event wiring
  // -------------------------------------------------------------------------

  bus.on('title:dismissed', () => {
    if (state.phase !== 'title') return
    state = { ...state, phase: 'investigating' }
    playBeatOnce(BEAT_INTRO)
  })

  bus.on('zone:changed', ({ zone }: { zone: ZoneId }) => {
    state = { ...state, zone }
    if (zone === 'indoor') playBeatOnce(BEAT_OFFICE)
  })

  bus.on('ui:journal-toggle', ({ open }) => {
    state = { ...state, journalOpen: open }
  })

  bus.on('player:interact', ({ target }) => {
    handleInteract(target)
  })

  bus.on('monologue:done', ({ beatId }) => {
    queue.notifyDone(beatId)
  })

  // -------------------------------------------------------------------------
  // QA automation — window.__gumshoe.state/collect/accuse
  // -------------------------------------------------------------------------

  debug.registerState(() => state)
  debug.registerCollect(collect)
  debug.registerAccuse(accuse)
}
